
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Home, Loader2 } from 'lucide-react';

const ConfirmSubscription = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const location = useLocation();
  
  useEffect(() => {
    const confirmSubscription = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        
        if (!token) {
          setStatus('error');
          setMessage('Missing confirmation token');
          return;
        }
        
        const { data, error } = await supabase.functions.invoke('confirm-subscription', {
          body: { token },
        });
        
        if (error) {
          setStatus('error');
          setMessage('Failed to confirm your subscription. Please try again.');
          console.error('Error confirming subscription:', error);
          return;
        }
        
        setStatus('success');
        setMessage(data.message || 'Your subscription has been confirmed!');
      } catch (error) {
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again later.');
        console.error('Unexpected error during confirmation:', error);
      }
    };
    
    confirmSubscription();
  }, [location.search]);
  
  return (
    <div className="container max-w-screen-md py-16">
      <div className="glass-card p-8 text-center border-futuristic-border/40">
        {status === 'loading' && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-blog-neon mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4">Confirming your subscription...</h1>
            <p className="text-muted-foreground mb-4">Please wait while we process your request.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-blog-neon/20 mb-6">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-8 w-8 text-blog-neon" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold mb-4">Subscription Confirmed!</h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <Link to="/">
              <Button className="bg-blog-neon hover:bg-blog-neon/90">
                <Home className="mr-2 h-4 w-4" />
                Return to Home
              </Button>
            </Link>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-red-500/20 mb-6">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-8 w-8 text-red-500" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold mb-4">Confirmation Failed</h1>
            <p className="text-muted-foreground mb-8">{message}</p>
            <Link to="/">
              <Button className="bg-blog-neon hover:bg-blog-neon/90">
                <Home className="mr-2 h-4 w-4" />
                Return to Home
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmSubscription;
